import type { MailDnsStatus, PublicMailIdentity } from '@embertime/shared'
import { Trash2 } from 'lucide-react'
import type { ReactNode } from 'react'
import { cn } from '../../lib/utils'
import { Button } from '../ui/button'

// Shared parts of the sender identity forms (#839): error texts, the switch
// rows for „Standard“/„Aktiv“ and the delete button of a saved identity.

/** Fixed German text for an identity API error; unknown errors keep the
 * server's message (the API sends German `error` texts). */
export function identityErrorText(err: unknown): string {
  const e = (err ?? {}) as { status?: unknown; code?: unknown; message?: unknown }
  const code = typeof e.code === 'string' ? e.code : undefined
  const status = typeof e.status === 'number' ? e.status : undefined
  if (code === 'MAIL_IDENTITY_EXISTS') return 'Diese Adresse ist schon als Absender angelegt.'
  if (code === 'MAIL_ACCOUNT_DISABLED') return 'Das Konto ist deaktiviert.'
  if (code === 'MAIL_SMTP_TARGET_BLOCKED') return 'Dieser Server ist nicht erlaubt.'
  if (code === 'MAIL_IDENTITY_PASSWORD_REQUIRED')
    return 'Neuer Server: bitte das Passwort neu eingeben.'
  if (code === 'INVALID_MAIL_IDENTITY') return 'Bitte die Eingaben prüfen.'
  if (code === 'MAIL_IDENTITY_RATE_LIMITED' || status === 429)
    return 'Zu viele Versuche. Bitte in einer Minute erneut versuchen.'
  if (status === 404) return 'Absender nicht gefunden.'
  if (typeof e.message === 'string' && e.message) return e.message
  return 'Das hat nicht geklappt.'
}

export function identityErrorCode(err: unknown): string | undefined {
  const code = (err as { code?: unknown } | null)?.code
  return typeof code === 'string' ? code : undefined
}

/** Domain part of an address (lower case), '' while there is none. */
export function addressDomain(address: string): string {
  const at = address.lastIndexOf('@')
  return at < 0
    ? ''
    : address
        .slice(at + 1)
        .trim()
        .toLowerCase()
}

/** HH:MM in German time notation, '' for a missing or broken date. */
export function clockTime(iso: string | null | undefined): string {
  if (!iso) return ''
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  return d.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })
}

export const DNS_STATUS_TEXT: Record<MailDnsStatus, string> = {
  ok: 'passt',
  missing: 'fehlt',
  invalid: 'ungültig',
  unknown: 'unbekannt',
}

/** Title of a form sheet by kind. */
export function identityKindTitle(kind: PublicMailIdentity['kind']): string {
  return kind === 'own_domain' ? 'Eigene Domain' : 'Fremder Postausgang'
}

function SwitchRow({
  label,
  hint,
  checked,
  disabled,
  onChange,
}: {
  label: string
  hint?: string
  checked: boolean
  disabled?: boolean
  onChange: (checked: boolean) => void
}) {
  return (
    <label className="flex min-h-12 items-center justify-between gap-3 py-2">
      <span className="min-w-0">
        <span className="block text-sm font-medium">{label}</span>
        {hint && <span className="block text-xs text-text-tertiary">{hint}</span>}
      </span>
      <input
        type="checkbox"
        role="switch"
        className="peer sr-only"
        aria-checked={checked}
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
      />
      <span
        aria-hidden="true"
        className="relative h-6 w-11 shrink-0 rounded-full border border-border-default bg-bg-overlay peer-checked:bg-accent peer-disabled:opacity-50 peer-focus-visible:outline peer-focus-visible:outline-2 peer-focus-visible:outline-accent after:absolute after:left-1 after:top-[3px] after:h-4 after:w-4 after:rounded-full after:bg-white after:transition-transform peer-checked:after:translate-x-5"
      />
    </label>
  )
}

/** „Standard“, „Aktiv“ and „Löschen“ of a saved identity. */
export function IdentityEditSettings({
  isDefault,
  active,
  disabled,
  onDefaultChange,
  onActiveChange,
  onDelete,
}: {
  isDefault: boolean
  active: boolean
  disabled?: boolean
  onDefaultChange: (value: boolean) => void
  onActiveChange: (value: boolean) => void
  onDelete: () => void
}) {
  return (
    <div className="divide-y divide-border-subtle rounded-xl border border-border-subtle px-3">
      <SwitchRow
        label="Standard"
        hint="Steht bei neuen Mails dieses Kontos bei „Von“"
        checked={isDefault && active}
        disabled={disabled || !active}
        onChange={onDefaultChange}
      />
      <SwitchRow
        label="Aktiv"
        hint={active ? 'Kann beim Schreiben gewählt werden' : 'Deaktiviert: nicht wählbar'}
        checked={active}
        disabled={disabled}
        onChange={onActiveChange}
      />
      <div className="py-2">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          disabled={disabled}
          onClick={onDelete}
          className="text-danger hover:text-danger"
        >
          <Trash2 className="h-4 w-4" aria-hidden="true" />
          Absender löschen
        </Button>
      </div>
    </div>
  )
}

/** Labelled form field block. */
export function Field({
  id,
  label,
  children,
  className,
}: {
  id: string
  label: string
  children: ReactNode
  className?: string
}) {
  return (
    <div className={cn('space-y-1.5', className)}>
      <label htmlFor={id} className="block text-sm text-text-secondary">
        {label}
      </label>
      {children}
    </div>
  )
}
